import { Box, chakra, useColorModeValue } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import React from 'react';

// Unified "live scanning" notice used across the explorer (Figma: node 1271-10379 container + 1271-10400 scan line).
// Renders the subtle container, the animated gradient line that sweeps along the top edge, and the caller's text.

// brand green (matches theme accent used across the explorer)
const BRAND = '20, 220, 172';

// gradient line segment translating across the top of the container (Figma node 1271:10400)
const scan = keyframes`
  0%   { left: 0%; transform: translateX(-100%); opacity: 0; }
  12%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { left: 100%; transform: translateX(0%); opacity: 0; }
`;

// animated trailing dots: "" → "." → ".." → "..."
const dotsFrames = keyframes`
  0% { content: ""; }
  25% { content: "."; }
  50% { content: ".."; }
  75%, 100% { content: "..."; }
`;

// bright gradient line that sweeps left → right along the top edge, with a soft green glow (Figma node 1271:10400)
const ScanLine = () => (
  <Box
    aria-hidden
    position="absolute"
    top={ 0 }
    left={ 0 }
    w="370px"
    h="1px"
    borderRadius="2px"
    pointerEvents="none"
    bg={ `linear-gradient(90deg, rgba(20, 20, 20, 1) 0%, rgba(${ BRAND }, 1) 100%)` }
    boxShadow={ `0px 2px 40px 0px rgba(${ BRAND }, 1)` }
    animation={ `${ scan } 2s linear infinite` }
    sx={{
      // when the user prefers reduced motion, keep a static (non-moving) line instead of the sweep
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        left: 0,
        w: '100%',
        bg: `linear-gradient(90deg, transparent 0%, rgba(${ BRAND }, 0.5) 50%, transparent 100%)`,
        boxShadow: 'none',
      },
    }}
  />
);

// animated "..." that reserves its own width so the text does not jitter
export const AnimatedDots = () => (
  <chakra.span
    display="inline-block"
    _after={{
      content: '""',
      display: 'inline-block',
      width: '1.2em',
      textAlign: 'left',
      animation: `${ dotsFrames } 1.5s linear infinite`,
    }}
    sx={{ '@media (prefers-reduced-motion: reduce)': { _after: { content: '"..."', animation: 'none' } } }}
  />
);

interface Props {
  children: React.ReactNode;
  className?: string;
}

const ScanningNotice = chakra(({ children, className }: Props) => {
  const bg = useColorModeValue(`rgba(${ BRAND }, 0.08)`, `rgba(${ BRAND }, 0.02)`);
  const color = useColorModeValue('blackAlpha.800', 'white');

  return (
    <Box
      role="status"
      aria-live="polite"
      className={ className }
      position="relative"
      overflow="hidden"
      bg={ bg }
      px="24px"
      py="6px"
      color={ color }
      fontSize="14px"
      lineHeight="20px"
      letterSpacing="-0.0107em"
    >
      <ScanLine/>
      <Box position="relative" zIndex={ 1 }>{ children }</Box>
    </Box>
  );
});

export default ScanningNotice;
